import React from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const sections = [
  {
    title: "Introduction",
    content:
      "This Cookie Policy explains how Axtelica Solutions Pvt Ltd uses cookies and similar technologies on our website and platforms.",
  },
  {
    title: "What Are Cookies?",
    content:
      "Cookies are small text files stored on your device when you visit websites or use online platforms. They help websites function properly, improve user experience, and provide analytics information.",
  },
  {
    title: "Types of Cookies We Use",
    content:
      "Essential Cookies: Required for core functionality such as user authentication, security, session management, and platform access.\n\nPerformance and Analytics Cookies: Used to measure website performance, analyze visitor behavior, and improve functionality and user experience.\n\nFunctional Cookies: Help remember user preferences, language settings, session information, and personalization data.\n\nSecurity Cookies: Used for fraud prevention, rate limiting, CAPTCHA validation, and threat detection.",
  },
  {
    title: "Third-Party Cookies",
    content:
      "Some third-party services integrated into our platforms may place cookies, including analytics providers, payment gateways, embedded tools, and cloud-based services. Third-party cookies are governed by their respective providers’ policies.",
  },
  {
    title: "Managing Cookies",
    content:
      "Most browsers allow users to view, delete, block, and manage cookie preferences. Disabling certain cookies may impact website functionality and user experience.",
  },
  {
    title: "Policy Updates",
    content:
      "We may update this Cookie Policy periodically. Changes will be published on our website with updated revision dates.",
  },
  {
    title: "Contact Information",
    content: (
      <>
        <p className="mb-2">Axtelica Solutions Pvt Ltd.</p>

        <p className="mb-2 break-all">
          Email:{" "}
          <a
            href="mailto:info@axtelica.com"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            info@axtelica.com
          </a>
        </p>

        <p className="break-all">
          Website:{" "}
          <a
            href="https://axtelica.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline transition"
          >
            Axtelica Official Website
          </a>
        </p>
      </>
    ),
  },
];

export default function CookiePolicy() {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="w-full overflow-hidden bg-white">
      {/* HERO SECTION */}
      <section
        className="
          relative flex items-center justify-center
          py-14 sm:py-16 lg:py-20
          min-h-[240px] sm:min-h-[320px] lg:min-h-[420px]
          w-full overflow-hidden
          bg-[linear-gradient(-45deg,#101030,#0F1242,#2558BA,#306BCA)]
          bg-[length:400%_400%]
          animate-gradientFlow
        "
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="
            relative z-10
            w-full
            max-w-[1400px]
            px-4 sm:px-6 lg:px-8
            flex justify-center
          "
        >
          <h1
            className="
              text-[30px]
              sm:text-[42px]
              md:text-[52px]
              lg:text-[64px]
              font-semibold
              text-white
              text-center
              leading-tight
              max-w-4xl
            "
          >
            Cookie Policy
          </h1>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="w-full py-8 sm:py-10 lg:py-14 px-3 sm:px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="
            max-w-[1400px]
            mx-auto
            rounded-2xl
          "
        >
          {/* SUBTITLE */}
          <p
            className="
              text-center
              text-gray-700
              text-base
              sm:text-lg
              md:text-xl
              lg:text-2xl
              leading-relaxed
              mb-8
              sm:mb-10
              px-2
            "
          >
            How Axtelica uses cookies and similar technologies.
          </p>

          {/* COOKIE POLICY SECTIONS */}
          <div className="space-y-5 sm:space-y-6">
            {sections.map((sec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                }}
                className="
                  border border-gray-200
                  rounded-2xl
                  p-4
                  sm:p-6
                  lg:p-7
                  bg-white
                  hover:shadow-lg
                  transition-all
                  duration-300
                "
              >
                {/* SECTION TITLE */}
                <h2
                  className="
                    text-xl
                    sm:text-2xl
                    lg:text-2xl
                    
                    text-gray-800
                    mb-3
                    leading-snug
                  "
                >
                  {sec.title}
                </h2>

                {/* SECTION CONTENT */}
                <div
                  className="
                    text-sm
                    sm:text-base
                    lg:text-lg
                    text-gray-600
                    leading-7
                    sm:leading-8
                    whitespace-pre-line
                    break-words
                  "
                >
                  {sec.content}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}